import { ATGSFLResponse } from 'types/ATGSFLResponse';
import type { ODMResult, ProductInfo } from 'types/ODMTypes';
import fetchWiremock from 'utilities/mock/fetchWiremock';
import { addLabels } from '../../utilities/loggerv2/performanceSpans';

const checkProductExistsInSaveForLater = async (
  mockEnabled: boolean,
  productId: string,
  skuId: string,
): Promise<ATGSFLResponse> => {
  if (mockEnabled) {
    return { productExists: false };
  }

  if (!window?.ODM?.s4l?.hasItems) {
    throw new Error('ODM S4L service is not available.');
  }

  try {
    const items: ProductInfo[] = [{ productId, skuId }];
    const result: ODMResult<Record<string, boolean>> = await window.ODM.s4l.hasItems(items);
    const responseKey = `${productId}-${skuId}`;
    const productExists = Boolean(result?.data?.[responseKey]);
    return { productExists };
  } catch (error) {
    console.error('Failed to check item in S4L', error);
    throw new Error('Failed to check item in S4L');
  }
};

const addProductToSaveForLater = async (mockEnabled: boolean, skuId: string) => {
  addLabels({
    name: 'Add Product to SFL',
    origin: 'PDP page',
  });
  if (mockEnabled) {
    const response = await fetchWiremock({
      method: 'PATCH',
      path: '/api/saveforlater/v1/list',
      body: { addSkus: [skuId] },
      headers: { 'x-mock-response': 'save-for-later-add' },
    });
    if (response.status !== 200) {
      addLabels({
        name: 'Add Product to SFL',
        origin: 'PDP page',
        outcome: 'Failure',
        status: response.status,
        isMock: true,
      });
      throw new Error(`Failed to add item to S4L. Wiremock response status: ${response.status}`);
    }
    return true;
  }

  if (!window?.ODM?.s4l?.updateList) {
    addLabels({
      name: 'Add Product to SFL',
      origin: 'PDP page',
      outcome: 'Failure',
      status: 'ODM Failure',
    });
    throw new Error('ODM S4L service is not available.');
  }

  try {
    const result = await window.ODM.s4l.updateList({ addSkus: [skuId] });
    if (!(result.status >= 200 && result.status < 300)) {
      addLabels({
        name: 'Add Product to SFL',
        origin: 'PDP page',
        outcome: 'Failure',
        status: result.status,
      });
      throw new Error(`Failed to add item to S4L. Response status: ${result.status}`);
    }
    addLabels({
      name: 'Add Product to SFL',
      origin: 'PDP page',
      outcome: 'Success',
      status: result.status,
    });
    return result;
  } catch (error) {
    addLabels({
      name: 'Add Product to SFL',
      origin: 'PDP page',
      outcome: 'Failure',
      error,
    });
    console.error('Failed to add item to S4L', error);
    throw new Error('Failed to add item to S4L');
  }
};

const removeProductFromSaveForLater = async (mockEnabled: boolean, skuId: string) => {
  addLabels({
    name: 'Remove Product to SFL',
    origin: 'PDP page',
  });
  if (mockEnabled) {
    const response = await fetchWiremock({
      method: 'PATCH',
      path: '/api/saveforlater/v1/list',
      body: { removeSkus: [skuId] },
      headers: { 'x-mock-response': 'save-for-later-remove' },
    });
    if (response.status !== 200) {
      addLabels({
        name: 'Remove Product to SFL',
        origin: 'PDP page',
        outcome: 'Failure',
        status: response.status,
        isMock: true,
      });
      throw new Error(`Failed to remove item from S4L. Wiremock response status: ${response.status}`);
    }
    return true;
  }
  if (!window?.ODM?.s4l?.updateList) {
    addLabels({
      name: 'Remove Product to SFL',
      origin: 'PDP page',
      outcome: 'Failure',
      status: 'ODM Failure',
    });
    throw new Error('ODM S4L service is not available.');
  }

  try {
    const result = await window.ODM.s4l.updateList({ removeSkus: [skuId] });
    if (!(result.status >= 200 && result.status < 300)) {
      addLabels({
        name: 'Remove Product to SFL',
        origin: 'PDP page',
        outcome: 'Failure',
        status: result.status,
      });
      throw new Error(`Failed to remove item to S4L. Response status: ${result.status}`);
    }
    addLabels({
      name: 'Remove Product to SFL',
      origin: 'PDP page',
      outcome: 'Success',
      status: result.status,
    });

    return result;
  } catch (error) {
    addLabels({
      name: 'Remove Product to SFL',
      origin: 'PDP page',
      outcome: 'Failure',
      error: 'error',
    });
    console.error('Failed to remove item from S4L', error);
    throw new Error('Failed to remove item from S4L');
  }
};

export default {
  checkProductExistsInSaveForLater,
  addProductToSaveForLater,
  removeProductFromSaveForLater,
};
